import dayjs from 'dayjs'
import type { UserActionsDetails, ModalField } from '../interfaces'
import { findReportLabelByUrl } from './reportTypeOptions'

// Detector para Consulta de Ocorrência
export function isOccurrenceConsultationReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/occurrence-consultation')
}

// Detector para Cronologia de Ocorrência (dispatch timeline)
export function isDispatchTimelineReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/dispatch-timeline')
}

// Detector para Ocorrências por tipo (/report/occurrence-detail-by-type)
export function isOccurrenceDetailByTypeReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/occurrence-detail-by-type')
}

// Detector para Ocorrências por dispositivo (/report/dispatches-by-device)
export function isDispatchesByDeviceReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/dispatches-by-device')
}

// Detector para Ocorrências por Grupo de Despacho e tipo (/report/occurrences)
export function isOccurrencesByDispatchGroupReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/occurrences')
}

// Detector para Totalização de ocorrência por grupo de despacho (/report/occurrences-handled-by-group)
export function isOccurrencesHandledByGroupReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/occurrences-handled-by-group')
}

// Detector para Ocorrências por data específica (/report/dispatches-status-by-date)
export function isDispatchesStatusByDateReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/dispatches-status-by-date')
}

// Detector para Pesquisa por múltiplos comentários (/report/search-comments)
export function isSearchCommentsReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/search-comments')
}

// Detector para Total de ocorrências por fechamento (/report/dispatch-by-closed-status)
export function isDispatchByClosedStatusReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/dispatch-by-closed-status')
}

// Detector para Ocorrências por Comandante (/report/dispatches-statistics-by-commander)
export function isDispatchesStatisticsByCommanderReport(details: UserActionsDetails): boolean {
  return !!(
    details.route === '/report/dispatches-statistics-by-commander' ||
    details.route === '/report/dispatches-by-commander'
  )
}

// Detector para Ocorrências por Comandante (payload armazenado no `content`)
export function isDispatchesByCommanderReport(details: UserActionsDetails): boolean {
  // Detect top-level route first
  if (details.route === '/report/dispatches-by-commander') return true

  // Some logs store the request params inside `content` as a JSON string or object
  // Try to detect the route there as well.
  const rawContent = (details as Record<string, unknown>)['content']
  if (!rawContent) return false

  if (typeof rawContent === 'string') {
    try {
      const parsed = JSON.parse(rawContent) as Record<string, unknown>
      return parsed.route === '/report/dispatches-by-commander'
    } catch {
      // not JSON — fall back to string search
      return rawContent.includes('/report/dispatches-by-commander')
    }
  }

  if (typeof rawContent === 'object' && rawContent !== null) {
    return (rawContent as Record<string, unknown>).route === '/report/dispatches-by-commander'
  }

  return false
}

// Detector para Lista de Dispositivos (/report/devices)
export function isDevicesReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/devices')
}

// Detector para Registro de Conexões (/report/connection-logs)
export function isConnectionLogsReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/connection-logs')
}

// Detector para Status histórico de dispositivo (/report/devices-by-status)
export function isDevicesByStatusReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/devices-by-status')
}

// Detector para Atendimento das Viaturas (/report/device-services)
export function isDeviceServicesReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/device-services')
}

// Detector para Status das viaturas x Ocorrências pendentes (/report/devices-vs-dispatches)
export function isDevicesVsDispatchesReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/devices-vs-dispatches')
}

// Detector para Total de dispositivos conectados (/report/connected-devices)
export function isConnectedDevicesReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/connected-devices')
}

// Detector para Cronologia do Dispositivo (/report/device-timeline)
export function isDeviceTimelineReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/device-timeline')
}

// Detector para Produtividade - Status Fora de Serviço (/report/out-of-service-productivity)
export function isOutOfServiceProductivityReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/out-of-service-productivity')
}

// Detector para Produtividade - Status Em Missão (/report/on-mission-productivity)
export function isOnMissionProductivityReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/on-mission-productivity')
}

// Detector para Teleatendimentos por status e usuário (/report/entry-by-status)
export function isEntryByStatusReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/entry-by-status')
}

// Detector para Total de atendimentos por usuário (/report/total-entry-by-user)
export function isTotalEntryByUserReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/total-entry-by-user')
}

// Detector para Total de trotes registrados (/report/total-prank-calls)
export function isTotalPrankCallsReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/total-prank-calls')
}

// Detector para Total de atendimentos por acurácia (/report/total-entry-by-accuracy)
export function isTotalEntryByAccuracyReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/total-entry-by-accuracy')
}

// Detector para Pontos de Interesse registrados (/report/interest-point)
export function isInterestPointReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/interest-point')
}

// Detector para Registro de Qualificadores (/report/qualifiers-records-by-options)
export function isQualifiersRecordsReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/qualifiers-records-by-options')
}

// Detector para Total de pesquisas de qualificadores por usuário (/report/total-qualifiers-searches-by-user)
export function isTotalQualifiersSearchesByUserReport(details: UserActionsDetails): boolean {
  return !!(details.route === '/report/total-qualifiers-searches-by-user')
}

// Detector para Lista de Usuários (/report/all-users)
export function isAllUsersReport(details: UserActionsDetails): boolean {
  if (details.route === '/report/all-users') return true

  const rawContent = (details as Record<string, unknown>)['content']
  if (!rawContent) return false

  if (typeof rawContent === 'string') {
    try {
      const parsed = JSON.parse(rawContent) as Record<string, unknown>
      return parsed.route === '/report/all-users'
    } catch {
      return rawContent.includes('/report/all-users')
    }
  }

  if (typeof rawContent === 'object' && rawContent !== null) {
    return (rawContent as Record<string, unknown>).route === '/report/all-users'
  }

  return false
}

// Formata um objeto de usuário (representation_name)
export function formatUserLabel(user: unknown): string | undefined {
  if (!user) return undefined
  if (typeof user === 'string') return user
  if (typeof user === 'object') {
    const obj = user as Record<string, unknown>
    const rep = (obj['representation_name'] ?? obj['name']) as string | undefined
    const registration = obj['registration'] as string | undefined

    if (typeof rep === 'string' && rep.trim()) {
      if (typeof registration === 'string' && registration.trim()) return `[${registration}] ${rep}`
      return rep
    }

    const login = obj['login']
    if (typeof login === 'string' || typeof login === 'number') return String(login)
    const id = obj['id']
    if (typeof id === 'string' || typeof id === 'number') return String(id)
  }

  return undefined
}

// Constrói campos base presentes em todos os relatórios
export function buildBaseUserFields(
  details: UserActionsDetails,
  translate: (key: string) => string
): readonly ModalField[] {
  const reportLabelFromRoute = details.route
    ? findReportLabelByUrl(details.route, translate)
    : undefined

  return [
    { 
      label: translate('User') || 'Usuário', 
      value: formatUserLabel(details.action_user) ?? details['user:representation_name'] ?? 'Não informado' 
    },
    {
      label: translate('Report') || 'Relatório',
      value: reportLabelFromRoute ?? 'Não informado',
    },
    {
      label: translate('action') || 'Ação',
      // some logs use request_type for API reports (ex: POST/GET) — show that if available
      value: (details.request_type as string | undefined) ?? 'Não informado',
    },
    {
      label: translate('Station') || 'Estação',
      value: details.action_station?.name ?? 'Não informado',
    },
    {
      label: translate('Performed at') || 'Realizado em',
      value: details.created_at
        ? dayjs(details.created_at).format('DD/MM/YYYY HH:mm')
        : 'Não informado',
    },
  ]
}

// Formata valor booleano para Sim/Não
export function formatBoolean(
  value: boolean | null | undefined,
  translate: (key: string) => string
): string {
  if (value == null) return 'Não informado'
  return value ? translate('Yes') : translate('No')
}

// Extrai e formata nome de objeto que pode ter propriedade name
export function extractObjectName(obj: unknown): string {
  if (!obj) return 'Não informado'
  if (typeof obj === 'string') return obj
  if (typeof obj === 'object' && obj !== null) {
    const name = (obj as Record<string, unknown>).name
    if (typeof name === 'string') return name
  }
  return 'Não informado'
}
